import type { TypologyItemTakeoffInput } from '@obraplan/core/application/contracts/purchase-from-takeoff';
import { consolidateItems } from '@obraplan/core/domain/takeoff/consolidation';
import { emptyCatalog, type ServiceCatalog } from '@obraplan/core/domain/takeoff/service-catalog';
import {
  bancadaDaObra,
  type LeiturasDoQuantitativo,
} from '@/features/schedule-items/takeoff-lines';
import { montarEscopoDaObra, recortarTipologia } from '@/features/takeoff/escopo-da-tipologia';
import { medidosDaTipologia, totaisDaTipologia } from '@/features/takeoff/medido-da-tipologia';
import {
  comodosDaTipologia,
  fundacaoDaTipologia,
  pileDaTipologia,
  quantitiesOf,
} from '@/features/takeoff/resumo-dos-itens';
import type { TypologyDraft, WorkspaceDraft } from '@/features/takeoff/typology-draft';

/**
 * O material que o cronograma por item recebe do quantitativo (Fase 35, RN-449).
 *
 * A irmã de `takeoff-lines.ts` (Fase 31), um nível abaixo: em vez do número de cada serviço, o
 * de cada **item comprável** do serviço — material, insumo, ferramenta e equipamento —, com a
 * quantidade que a tipologia resolve para ele (`resolveItemQuantity`: digitada, das peças
 * importadas, calculada pelo índice ou da planilha). É a mesma conta da Consolidação e da aba
 * Orçamento, tipologia a tipologia (`consolidateItems`).
 *
 * O item vai pela chave da Consolidação (`codigo:…` ou `descricao:…`), que é a mesma em todas as
 * tipologias; o servidor soma por pavimento pelas posições do desenho e acha o material.
 */
export function materiaisDaBancada(
  workspace: WorkspaceDraft,
  recorteDe: (typology: TypologyDraft) => ServiceCatalog,
  daObra: ReadonlySet<string>,
): TypologyItemTakeoffInput[] {
  const linhas: TypologyItemTakeoffInput[] = [];
  for (const typology of workspace.typologies) {
    if (!daObra.has(String(typology.unitTypeId))) continue;
    const catalog = recorteDe(typology);
    if (catalog.disciplines.length === 0) continue;

    const totals = totaisDaTipologia(workspace, typology);
    // A fundação com fator e taxa por peça (Fase 41): o medido do aço e a parte de cada peça.
    const fundacao = fundacaoDaTipologia(workspace, typology);
    const quantities = quantitiesOf(
      typology,
      medidosDaTipologia(catalog, totals, typology, fundacao),
      catalog,
      pileDaTipologia(typology, workspace.pileTypes),
      comodosDaTipologia(workspace, typology),
      fundacao,
    );

    for (const item of consolidateItems(catalog, quantities)) {
      // Mão de obra não vira compra (RN-166): ela já foi para o cronograma por mão de obra.
      if (item.kind === 'MAO_DE_OBRA') continue;
      for (const uso of item.usages) {
        if (!Number.isFinite(uso.quantity) || uso.quantity <= 0) continue;
        linhas.push({
          unitTypeId: String(typology.unitTypeId),
          disciplineName: uso.disciplineName,
          serviceName: uso.serviceName,
          itemKey: item.key,
          code: item.code ?? null,
          description: item.description,
          unit: item.unit ?? null,
          kind: item.kind,
          quantity: uso.quantity,
        });
      }
    }
  }
  return linhas;
}

/**
 * O material da obra inteira a partir das cinco leituras: a bancada, o recorte de cada
 * tipologia (o que ela escolheu, nas disciplinas dos andares dela) e as linhas.
 */
export function materiaisDaObra(leituras: LeiturasDoQuantitativo): TypologyItemTakeoffInput[] {
  const workspace = bancadaDaObra(leituras);
  const escopo = montarEscopoDaObra(leituras.estrutura, leituras.quantitativo, leituras.servicos);
  const catalogo = workspace.catalog ?? emptyCatalog();
  return materiaisDaBancada(
    workspace,
    (typology) => recortarTipologia(catalogo, typology, escopo).catalogo,
    new Set(leituras.quantitativo.unitTypes.map((unitType) => String(unitType.id))),
  );
}
